import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Textarea,
} from '@cloudscape-design/components';
import { SETUP_TYPE, SUB_MODE } from 'features/plc/contracts/testStand.schema';
import updateTestStandLog from 'features/testStand/api/updateTestStandLog';
import useTestStandLogs from 'features/testStand/hooks/useTestStandLogs';
import getChannelInfo from 'features/testStand/utils/getChannelInfo';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useTestStand } from '../../providers/TestStandProvider';
import RunChart from './RunChart';

const RUN_STATUS_TYPE = {
  draft: 'info',
  started: 'in-progress',
  running: 'in-progress',
  complete: 'success',
  error: 'error',
  aborted: 'stopped',
};

function formatMs(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function setupLabel(setup) {
  if (setup === SETUP_TYPE.CONTINUOUS_FLOW) return 'Continuous Flow';
  if (setup === SETUP_TYPE.DISCRETE_FLOW) return 'Discrete Flow';
  return null;
}

function subModeLabel(setup, subMode) {
  if (setup !== SETUP_TYPE.DISCRETE_FLOW) return null;
  if (subMode === SUB_MODE.PRESSURE || subMode === 'pressure') return 'Pressure';
  if (subMode === SUB_MODE.DISPLACEMENT || subMode === 'displacement') return 'Displacement';
  return null;
}

function buildHeaderText(run) {
  const parts = [run.testName || '(no name)'];
  const setup = setupLabel(run.setup);
  if (setup) parts.push(setup);
  const sub = subModeLabel(run.setup, run.subMode);
  if (sub) parts.push(sub);
  if (run.createdAt) {
    parts.push(new Date(run.createdAt).toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }));
  }
  return parts.join(' — ');
}

function ParamSnapshot({ params = null }) {
  if (!params) return <Box color="text-status-inactive">No parameter snapshot.</Box>;
  return (
    <ColumnLayout columns={3} variant="text-grid">
      {Object.entries(params).map(([k, v]) => (
        <div key={k}>
          <Box variant="awsui-key-label">{k}</Box>
          <Box>{String(v)}</Box>
        </div>
      ))}
    </ColumnLayout>
  );
}
ParamSnapshot.propTypes = {
  params: PropTypes.shape({}),
};

function buildDraft(run) {
  return {
    testNotes: run.testNotes || '',
    lipidInfo: getChannelInfo(run.channels?.lipid),
    aqueousInfo: getChannelInfo(run.channels?.aqueous),
    cameraLink: run.cameraLink || '',
    flowRateLink: run.flowRateLink || '',
  };
}

function buildPatch(draft) {
  return {
    testNotes: draft.testNotes,
    'channels.lipid.info': draft.lipidInfo,
    'channels.aqueous.info': draft.aqueousInfo,
    cameraLink: draft.cameraLink,
    flowRateLink: draft.flowRateLink,
  };
}

function RunRow({ run }) {
  const {
    state: { expandedRunId } = {},
    setExpandedRun,
  } = useTestStand();
  const isExpanded = expandedRunId === run.runId;

  const firestoreDraft = buildDraft(run);
  const [draft, setDraft] = useState(firestoreDraft);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Sync from Firestore when not dirty (picks up cross-tab edits).
  // When dirty, local draft wins until the user explicitly saves.
  useEffect(() => {
    if (!isDirty) setDraft(firestoreDraft);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]); // re-run whenever Firestore pushes a new run object

  const setField = useCallback((key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setIsDirty(true);
    setSaveError('');
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      await updateTestStandLog(run.runId, buildPatch(draft));
      setIsDirty(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [draft, run.runId]);

  const handleExpand = useCallback((expanded) => {
    setExpandedRun(expanded ? run.runId : null);
  }, [run.runId, setExpandedRun]);

  return (
    <ExpandableSection
      headerText={buildHeaderText(run)}
      expanded={isExpanded}
      onChange={({ detail }) => handleExpand(detail.expanded)}
    >
      <SpaceBetween size="m">
        {isExpanded && (
          <RunChart run={run} />
        )}
        <SpaceBetween size="s">
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Log ID</Box>
              <Box variant="code">{run.runId}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Started</Box>
              <Box>{formatMs(run.startedAt)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Stopped</Box>
              <Box>{formatMs(run.stoppedAt)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <StatusIndicator type={RUN_STATUS_TYPE[run.status] || 'info'}>
                {run.status || '—'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
          <ColumnLayout columns={2}>
            <FormField label="Test notes">
              <Textarea
                value={draft.testNotes}
                onChange={({ detail }) => setField('testNotes', detail.value)}
                placeholder="Notes for the whole test"
              />
            </FormField>
            <SpaceBetween size="s">
              <FormField label="Camera link">
                <Input
                  value={draft.cameraLink}
                  onChange={({ detail }) => setField('cameraLink', detail.value)}
                  placeholder="Paste camera data link"
                />
              </FormField>
              <FormField label="Flow rate link">
                <Input
                  value={draft.flowRateLink}
                  onChange={({ detail }) => setField('flowRateLink', detail.value)}
                  placeholder="Paste flow rate data link"
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
          <ColumnLayout columns={2}>
            <FormField label="Lipid channel info">
              <Textarea
                value={draft.lipidInfo}
                onChange={({ detail }) => setField('lipidInfo', detail.value)}
                placeholder="Liquid name and lipid channel notes"
              />
            </FormField>
            <FormField label="Aqueous channel info">
              <Textarea
                value={draft.aqueousInfo}
                onChange={({ detail }) => setField('aqueousInfo', detail.value)}
                placeholder="Liquid name and aqueous channel notes"
              />
            </FormField>
          </ColumnLayout>
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              variant="primary"
              loading={isSaving}
              disabled={!isDirty}
              onClick={handleSave}
            >
              Save
            </Button>
            {saveError ? (
              <StatusIndicator type="error">{saveError}</StatusIndicator>
            ) : null}
          </SpaceBetween>
        </SpaceBetween>
        <SpaceBetween size="s">
          <Box variant="awsui-key-label" margin={{ top: 's' }}>Lipid parameters</Box>
          <ParamSnapshot params={run.channels?.lipid?.params} />
          <Box variant="awsui-key-label" margin={{ top: 's' }}>Aqueous parameters</Box>
          <ParamSnapshot params={run.channels?.aqueous?.params} />
        </SpaceBetween>
      </SpaceBetween>
    </ExpandableSection>
  );
}
RunRow.propTypes = {
  run: PropTypes.shape({
    runId: PropTypes.string.isRequired,
    testName: PropTypes.string,
    setup: PropTypes.number,
    createdAt: PropTypes.number,
    updatedAt: PropTypes.number,
    startedAt: PropTypes.number,
    stoppedAt: PropTypes.number,
    status: PropTypes.string,
    subMode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    channels: PropTypes.shape({
      lipid: PropTypes.shape({
        info: PropTypes.string,
        liquidName: PropTypes.string,
        notes: PropTypes.string,
        params: PropTypes.shape({}),
      }),
      aqueous: PropTypes.shape({
        info: PropTypes.string,
        liquidName: PropTypes.string,
        notes: PropTypes.string,
        params: PropTypes.shape({}),
      }),
    }),
    testNotes: PropTypes.string,
    cameraLink: PropTypes.string,
    flowRateLink: PropTypes.string,
  }).isRequired,
};

const PAGE_SIZE = 20;

function RunLogViewer() {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { runs, loading, error } = useTestStandLogs({ maxCount: limit });
  const hasMore = runs.length === limit;

  // Clear the loading state as soon as any snapshot arrives after clicking "Show more".
  useEffect(() => {
    setIsFetchingMore(false);
  }, [runs]);

  const handleShowMore = useCallback(() => {
    setIsFetchingMore(true);
    setLimit((l) => l + PAGE_SIZE);
  }, []);

  return (
    <Container header={<Header variant="h2">Run Log</Header>}>
      {loading && (
        <Box textAlign="center" padding="m"><Spinner /></Box>
      )}
      {error && (
        <Box color="text-status-error">
          Failed to load run log:
          {' '}
          {error}
        </Box>
      )}
      {!loading && runs.length === 0 && !error && (
        <Box color="text-status-inactive" textAlign="center" padding="m">
          No runs recorded yet.
        </Box>
      )}
      <SpaceBetween size="xs">
        {runs.map((run) => <RunRow key={run.runId} run={run} />)}
        {!loading && (hasMore || isFetchingMore) && (
          <Box textAlign="center" padding={{ top: 's' }}>
            <Button
              variant="link"
              loading={isFetchingMore}
              onClick={handleShowMore}
            >
              Show 20 more
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}

export default RunLogViewer;
